// member-portal/src/components/SuperAdminPromoterPayouts.jsx — FULL FILE
// UPDATED: Show "Mark Paid" button after Approve (status === 'approved')
// - Buttons now conditional on status
// - All existing layout preserved

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import './SuperAdminPromoterPayouts.css';

Modal.setAppElement('#root');

const SuperAdminPromoterPayouts = ({ user }) => {
  const [promoters, setPromoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPromoter, setSelectedPromoter] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/superadmin/promoter-payouts?search=${encodeURIComponent(search)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPromoters(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load promoters');
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (promoter) => {
    setSelectedPromoter(promoter);
    setModalOpen(true);
    setDetailsLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/superadmin/promoter-payouts/${promoter._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDetails(data);
    } catch (err) {
      toast.error('Failed to load details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setDetails(null);
    setSelectedPromoter(null);
  };

  const handleAction = async (requestId, action, extra = {}) => {
    if (actionLoading[requestId]) return;
    setActionLoading(prev => ({ ...prev, [requestId]: true }));

    try {
      const token = localStorage.getItem('memberToken');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/superadmin/promoter-payouts/${requestId}/action`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action, ...extra }),
        }
      );

      if (!res.ok) throw new Error(await res.text());

      toast.success(`Payout ${action}d`);
      fetchData();
      if (modalOpen) openModal(selectedPromoter);
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const filtered = promoters.filter(p =>
    `${p.name || ''} ${p.surname || ''} ${p.username} ${p.email || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="superadmin-payouts">
      <h2>Promoter Payout Requests</h2>

      <input
        type="text"
        placeholder="Search promoters..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="search-input"
      />

      {loading ? (
        <div className="loading">Loading...</div>
      ) : filtered.length === 0 ? (
        <p className="no-data">No promoters found</p>
      ) : (
        filtered.map(p => (
          <div key={p._id} className="promoter-card">
            <div className="promoter-header">
              <div className="promoter-info">
                <strong>{p.name || ''} {p.surname || ''}</strong>
                <span className="username">@{p.username}</span>
                <div className="email">{p.email}</div>
              </div>
              <div className="summary">
                <span className="earnings">R{p.totalEarnings.toFixed(2)}</span>
                <span className="referred">{p.referredCount} restaurants</span>
                <button
                  onClick={() => openModal(p)}
                  className="view-details-btn"
                >
                  View Details
                </button>
              </div>
            </div>

            {expanded === p._id && (
              <div className="payout-list">
                {p.payoutRequests?.length === 0 ? (
                  <p className="no-data">No payout requests yet</p>
                ) : (
                  p.payoutRequests.map(r => (
                    <div key={r._id} className="payout-item">
                      <div className="payout-details">
                        <div className="amount">R {r.requestedAmount.toFixed(2)}</div>
                        <div className="date">{new Date(r.requestedAt).toLocaleString('en-ZA')}</div>
                        <div className={`status ${r.status}`}>{r.status.toUpperCase()}</div>
                        {r.notes && <div className="notes">Notes: {r.notes}</div>}
                        {r.paymentReference && <div className="ref">Ref: {r.paymentReference}</div>}
                      </div>

                      {r.status === 'pending' && (
                        <div className="payout-actions">
                          <button
                            onClick={() => handleAction(r._id, 'approve')}
                            disabled={actionLoading[r._id]}
                            className="action-btn approve"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              const notes = prompt('Rejection notes (optional):');
                              if (notes !== null) handleAction(r._id, 'reject', { notes });
                            }}
                            disabled={actionLoading[r._id]}
                            className="action-btn reject"
                          >
                            Reject
                          </button>
                        </div>
                      )}

                      {r.status === 'approved' && (
                        <div className="payout-actions">
                          <button
                            onClick={() => {
                              const ref = prompt('Payment reference (EFT ID):');
                              if (ref) handleAction(r._id, 'paid', { paymentReference: ref });
                            }}
                            disabled={actionLoading[r._id]}
                            className="action-btn paid"
                          >
                            Mark Paid
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))
      )}

      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        className="promoter-modal"
        overlayClassName="modal-overlay"
      >
        {detailsLoading ? (
          <div className="modal-loading">Loading details...</div>
        ) : details ? (
          <div className="modal-content">
            <h2 className="modal-title">{details.promoter.name} {details.promoter.surname}</h2>
            <div className="modal-profile">
              <p><strong>Username:</strong> @{details.promoter.username}</p>
              <p><strong>Email:</strong> {details.promoter.email}</p>
              <p><strong>Referral Code:</strong> {details.promoter.referralCode}</p>
              <p><strong>Total Earnings:</strong> R{details.promoter.totalEarnings.toFixed(2)}</p>
            </div>

            <h3>Bank Details</h3>
            {details.promoter.bankDetails && details.promoter.bankDetails.accountHolder ? (
              <div className="bank-info">
                <p><strong>Holder:</strong> {details.promoter.bankDetails.accountHolder}</p>
                <p><strong>Bank:</strong> {details.promoter.bankDetails.bankName}</p>
                <p><strong>Account Number:</strong> {details.promoter.bankDetails.accountNumber}</p>
                <p><strong>Branch Code:</strong> {details.promoter.bankDetails.branchCode}</p>
              </div>
            ) : (
              <p className="no-data">No bank details saved yet.</p>
            )}

            <h3>Earnings Breakdown</h3>
            {details.earningsBreakdown ? (
              <div className="earnings-breakdown">
                <p><strong>Once-Off Bonuses:</strong> R{details.earningsBreakdown.totalOnceOff.toFixed(2)}</p>
                {details.earningsBreakdown.onceOffBreakdown.map((b, i) => (
                  <p key={i} className="breakdown-item">
                    • {b.companyName}: R{b.amount} ({b.note})
                  </p>
                ))}

                <p><strong>Current Month Recurring:</strong> R{details.earningsBreakdown.currentMonthlyRecurring.toFixed(2)}</p>
                {details.earningsBreakdown.currentMonthlyBreakdown.map((b, i) => (
                  <p key={i} className="breakdown-item">
                    • {b.companyName}: R{b.amount} ({b.rate}% on R{b.revenue})
                  </p>
                ))}

                {details.earningsBreakdown.historicalEarnings.map((m, i) => (
                  <div key={i} className="historical-month">
                    <p><strong>{m.month}:</strong> R{m.totalRecurring.toFixed(2)}</p>
                    {m.breakdown.map((b, j) => (
                      <p key={j} className="breakdown-item">
                        • {b.companyName}: R{b.amount} ({b.rate}% on R{b.revenue})
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <p>No earnings breakdown available.</p>
            )}

            <h3>Payout Requests</h3>
            {details.payoutRequests.length === 0 ? (
              <p className="no-data">No payout requests yet</p>
            ) : (
              details.payoutRequests.map(r => (
                <div key={r._id} className="modal-payout">
                  <div className="payout-header">
                    <span className="amount">R{r.requestedAmount.toFixed(2)}</span>
                    <span className="date">{new Date(r.requestedAt).toLocaleString('en-ZA')}</span>
                    <span className={`status ${r.status}`}>{r.status.toUpperCase()}</span>
                  </div>
                  {r.notes && <p className="notes">Notes: {r.notes}</p>}
                  {r.paymentReference && <p className="ref">Ref: {r.paymentReference}</p>}
                  {r.status === 'pending' && (
                    <div className="modal-actions">
                      <button onClick={() => handleAction(r._id, 'approve')} className="action-btn approve">Approve</button>
                      <button onClick={() => {
                        const notes = prompt('Rejection notes (optional):');
                        if (notes !== null) handleAction(r._id, 'reject', { notes });
                      }} className="action-btn reject">Reject</button>
                    </div>
                  )}
                  {r.status === 'approved' && (
                    <div className="modal-actions">
                      <button onClick={() => {
                        const ref = prompt('Payment reference (EFT ID):');
                        if (ref) handleAction(r._id, 'paid', { paymentReference: ref });
                      }} className="action-btn paid">Mark Paid</button>
                    </div>
                  )}
                </div>
              ))
            )}

            <button onClick={closeModal} className="close-btn">Close</button>
          </div>
        ) : (
          <p className="error">Error loading details</p>
        )}
      </Modal>
    </div>
  );
};

export default SuperAdminPromoterPayouts;